import React, { useState, useEffect } from "react";
import { 
  Package, 
  Search, 
  Filter, 
  Calendar, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  User,
  CheckCircle,
  Clock,
  TrendingUp,
  Download,
  X,
  Eye,
  ShoppingCart,
  FileText,
  ArrowUpCircle,
  ArrowDownCircle,
  Box,
  Activity
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Siparisler() {
  const [siparisler, setSiparisler] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ozet, setOzet] = useState(null);
  const [plasiyerler, setPlasiyerler] = useState([]);
  
  // Hafıza/Cache state
  const [hafizadan, setHafizadan] = useState(false);
  const [sonSync, setSonSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  
  // Detay modal state
  const [detayModal, setDetayModal] = useState(false);
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [siparisDetay, setSiparisDetay] = useState(null);
  const [detayLoading, setDetayLoading] = useState(false);
  
  // Stok hareket modal state
  const [stokHareketModal, setStokHareketModal] = useState(false);
  const [selectedStok, setSelectedStok] = useState(null);
  const [stokHareket, setStokHareket] = useState(null);
  const [stokHareketLoading, setStokHareketLoading] = useState(false);
  
  // Filtreler
  const [filters, setFilters] = useState({
    sirket: "miba",
    baslangicTarihi: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    bitisTarihi: new Date().toISOString().split('T')[0],
    cariKodu: "",
    plasiyer: "",
    durum: "",
    arama: "",
    limit: 50,
    offset: 0
  });

  const [showFilters, setShowFilters] = useState(false);

  // İlk yüklemede hafızadan yükle ve plasiyerleri çek
  useEffect(() => {
    fetchPlasiyerler();
    // Önce hafızadan yükle
    fetchSiparislerHafiza();
  }, []);

  // Filtre değiştiğinde siparişleri yeniden çek
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchSiparislerHafiza();
    }, 100);
    return () => clearTimeout(timer);
  }, [filters.baslangicTarihi, filters.bitisTarihi, filters.plasiyer, filters.durum, filters.arama]);

  // Sayfa değiştiğinde
  useEffect(() => {
    if (page > 1) {
      fetchSiparislerHafiza();
    }
  }, [page]);

  const fetchPlasiyerler = async () => {
    try {
      const response = await fetch(`${API_URL}/api/siparisler/plasiyerler?sirket=${filters.sirket}`);
      const data = await response.json();
      if (data.success) {
        setPlasiyerler(data.plasiyerler);
      }
    } catch (error) {
      console.error("Plasiyer çekme hatası:", error);
    }
  };

  // Hafızadan siparişleri getir (hızlı)
  const fetchSiparislerHafiza = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/siparisler/hafiza?sirket=${filters.sirket}&page=${page}&limit=${filters.limit}`;
      if (filters.baslangicTarihi) url += `&baslangicTarihi=${filters.baslangicTarihi}`;
      if (filters.bitisTarihi) url += `&bitisTarihi=${filters.bitisTarihi}`;
      if (filters.plasiyer) url += `&plasiyer=${encodeURIComponent(filters.plasiyer)}`;
      if (filters.durum) url += `&durum=${filters.durum}`;
      if (filters.arama) url += `&arama=${encodeURIComponent(filters.arama)}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setSiparisler(data.siparisler);
        setToplam(data.toplam);
        setOzet(data.ozet);
        setHafizadan(data.hafizadan);
        setSonSync(data.sonSync);
        
        // Eğer hafızada veri yoksa otomatik sync başlat
        if (data.toplam === 0 && !data.sonSync) {
          toast.info("İlk yükleme - Netsis'den çekiliyor...");
          await syncSiparisler(false);
        }
      } else {
        toast.error(data.detail || "Siparişler çekilemedi");
      }
    } catch (error) {
      console.error("Sipariş hafıza çekme hatası:", error);
      // Hafıza hatası varsa direkt Netsis'den çek
      await fetchSiparislerDirekt();
    } finally {
      setLoading(false);
    }
  };

  // Direkt Netsis'den çek (yedek)
  const fetchSiparislerDirekt = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/siparisler`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({...filters, offset: (page - 1) * filters.limit})
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSiparisler(data.siparisler);
        setToplam(data.toplam);
        setHafizadan(false);
      } else {
        toast.error(data.detail || "Siparişler çekilemedi");
      }
    } catch (error) {
      console.error("Sipariş çekme hatası:", error);
      toast.error("Sunucu bağlantı hatası");
    } finally {
      setLoading(false);
    }
  };

  // Netsis'den sync et ve hafızaya kaydet
  const syncSiparisler = async (tamSync = false) => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/siparisler/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sirket: filters.sirket,
          baslangicTarihi: filters.baslangicTarihi,
          bitisTarihi: filters.bitisTarihi,
          tamSync: tamSync
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Senkronizasyon tamamlandı: ${data.yeniKayit} yeni, ${data.guncellenen} güncellendi`);
        // Hafızadan tekrar yükle
        await fetchSiparislerHafiza();
      } else if (data.hata === "BAGLANTI_HATASI") {
        // Netsis bağlantı hatası - bilgilendirici mesaj
        toast.warning(
          <div>
            <p className="font-semibold">Netsis Bağlantısı Kurulamadı</p>
            <p className="text-sm mt-1">Preview ortamından bağlanılamıyor. Mevcut {data.hafizadakiToplam || 0} sipariş hafızada.</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error(data.mesaj || data.detail || "Senkronizasyon hatası");
      }
    } catch (error) {
      console.error("Sync hatası:", error);
      toast.error("Senkronizasyon hatası: Sunucuya bağlanılamadı");
    } finally {
      setSyncing(false);
    }
  };

  const fetchSiparisDetay = async (siparisNo) => {
    setDetayLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/siparisler/detay/${siparisNo}?sirket=${filters.sirket}`);
      const data = await response.json();
      
      if (data.success) {
        setSiparisDetay(data);
      } else {
        toast.error(data.detail || "Sipariş detayı çekilemedi");
      }
    } catch (error) {
      console.error("Sipariş detay hatası:", error);
      toast.error("Sunucu bağlantı hatası");
    } finally {
      setDetayLoading(false);
    }
  };

  const fetchStokHareket = async (stokKodu) => {
    setStokHareketLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stok/hareket/${encodeURIComponent(stokKodu)}?sirket=${filters.sirket}`);
      const data = await response.json();
      
      if (data.success) {
        setStokHareket(data);
      } else {
        toast.error(data.detail || "Stok hareketi çekilemedi");
      }
    } catch (error) {
      console.error("Stok hareket hatası:", error);
      toast.error("Sunucu bağlantı hatası");
    } finally {
      setStokHareketLoading(false);
    }
  };

  const openDetayModal = (siparis) => {
    setSelectedSiparis(siparis);
    setDetayModal(true);
    fetchSiparisDetay(siparis.siparisNo);
  };

  const closeDetayModal = () => {
    setDetayModal(false);
    setSelectedSiparis(null);
    setSiparisDetay(null);
  };

  const openStokHareketModal = (stokKodu, stokAdi) => {
    setSelectedStok({ stokKodu, stokAdi });
    setStokHareketModal(true);
    fetchStokHareket(stokKodu);
  };

  const closeStokHareketModal = () => {
    setStokHareketModal(false);
    setSelectedStok(null);
    setStokHareket(null);
  };

  const handleSearch = () => {
    setPage(1);
    fetchSiparislerHafiza();
  };

  const handlePageChange = (direction) => {
    if (direction === 'next') {
      setPage(prev => prev + 1);
    } else {
      setPage(prev => Math.max(1, prev - 1));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const totalPages = Math.ceil(toplam / filters.limit);

  // Son sync zamanını formatla
  const formatSyncTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleString('tr-TR', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-textPrimary flex items-center gap-2">
            <Package className="h-7 w-7 text-primary" />
            Siparişler
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-textSecondary text-sm">
              {hafizadan ? "📦 Hafızadan yüklendi" : "🌐 Netsis'den yüklendi"}
            </p>
            {sonSync && (
              <span className="text-xs text-textSecondary">
                • Son sync: {formatSyncTime(sonSync)}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters ? 'bg-primary text-white border-primary' : 'border-border hover:bg-surfaceSubtle'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filtreler
          </button>
          
          <button
            onClick={() => syncSiparisler(false)}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            title="Netsis'den yeni siparişleri çek"
          >
            <Download className={`h-4 w-4 ${syncing ? 'animate-bounce' : ''}`} />
            {syncing ? "Çekiliyor..." : "Güncelle"}
          </button>
          
          <button
            onClick={() => fetchSiparislerHafiza()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </button>
        </div>
      </div>

      {/* Özet Kartları */}
      {ozet && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Package className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-textSecondary">Toplam Sipariş</p>
                <p className="text-xl font-bold text-textPrimary">{ozet.toplamSiparis || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-textSecondary">Kapalı Sipariş</p>
                <p className="text-xl font-bold text-green-600">{ozet.kapaliSiparis || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Clock className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-textSecondary">Açık Sipariş</p>
                <p className="text-xl font-bold text-orange-600">{ozet.acikSiparis || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-textSecondary">Toplam Tutar</p>
                <p className="text-lg font-bold text-textPrimary">{formatCurrency(ozet.toplamTutar)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtreler */}
      {showFilters && (
        <div className="bg-surface border border-border rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Tarih Aralığı */}
            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">Başlangıç Tarihi</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
                <input
                  type="date"
                  value={filters.baslangicTarihi}
                  onChange={(e) => setFilters(prev => ({ ...prev, baslangicTarihi: e.target.value, offset: 0 }))}
                  className="w-full pl-10 pr-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">Bitiş Tarihi</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
                <input
                  type="date"
                  value={filters.bitisTarihi}
                  onChange={(e) => setFilters(prev => ({ ...prev, bitisTarihi: e.target.value, offset: 0 }))}
                  className="w-full pl-10 pr-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
            
            {/* Plasiyer */}
            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">Plasiyer</label>
              <select
                value={filters.plasiyer}
                onChange={(e) => setFilters(prev => ({ ...prev, plasiyer: e.target.value, offset: 0 }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Tümü</option>
                {plasiyerler.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            
            {/* Durum */}
            <div>
              <label className="block text-xs font-medium text-textSecondary mb-1">Durum</label>
              <select
                value={filters.durum}
                onChange={(e) => setFilters(prev => ({ ...prev, durum: e.target.value, offset: 0 }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Tümü</option>
                <option value="acik">Açık</option>
                <option value="kapali">Kapalı</option>
              </select>
            </div>
          </div>
          
          {/* Arama */}
          <div className="mt-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
              <input
                type="text"
                placeholder="Sipariş no, cari kodu veya açıklama ara..."
                value={filters.arama}
                onChange={(e) => setFilters(prev => ({ ...prev, arama: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Ara
            </button>
          </div>
        </div>
      )}

      {/* Sipariş Tablosu */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-surfaceSubtle border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-semibold text-textSecondary uppercase tracking-wider">Sipariş No</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-textSecondary uppercase tracking-wider">Tarih</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-textSecondary uppercase tracking-wider">Cari</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-textSecondary uppercase tracking-wider">Plasiyer</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-textSecondary uppercase tracking-wider">Tutar</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-textSecondary uppercase tracking-wider">Durum / Fatura</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
                    <p className="text-textSecondary">Yükleniyor...</p>
                  </td>
                </tr>
              ) : siparisler.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Package className="h-12 w-12 text-textSecondary/30 mx-auto mb-2" />
                    <p className="text-textSecondary">Sipariş bulunamadı</p>
                  </td>
                </tr>
              ) : (
                siparisler.map((siparis, idx) => (
                  <tr 
                    key={idx} 
                    className="hover:bg-surfaceSubtle/50 transition-colors cursor-pointer"
                    onClick={() => openDetayModal(siparis)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-textSecondary/50" />
                        <span className="font-mono text-sm text-primary">{siparis.siparisNo}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-textPrimary">
                      {formatDate(siparis.tarih)}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-textPrimary truncate max-w-[200px]">
                          {siparis.cariIsim || siparis.cariKodu}
                        </p>
                        {siparis.cariIsim && (
                          <p className="text-xs text-textSecondary">{siparis.cariKodu}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-600 rounded text-xs font-medium">
                        <User className="h-3 w-3" />
                        {siparis.plasiyer || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-textPrimary">
                        {formatCurrency(siparis.genelToplam)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          siparis.durum === 'KAPALI' 
                            ? 'bg-green-500/10 text-green-600' 
                            : 'bg-orange-500/10 text-orange-600'
                        }`}>
                          {siparis.durum === 'KAPALI' ? (
                            <><CheckCircle className="h-3 w-3" /> Kapalı</>
                          ) : (
                            <><Clock className="h-3 w-3" /> Açık</>
                          )}
                        </span>
                        {siparis.faturaNo && (
                          <span className="text-xs font-mono text-purple-600 bg-purple-500/10 px-2 py-0.5 rounded">
                            {siparis.faturaNo}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {toplam > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surfaceSubtle/50">
            <p className="text-sm text-textSecondary">
              Toplam <span className="font-semibold text-textPrimary">{toplam}</span> sipariş
              {toplam > filters.limit && (
                <span> (Sayfa {page}/{totalPages})</span>
              )}
            </p>
            
            {toplam > filters.limit && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange('prev')}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-border hover:bg-surfaceSubtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange('next')}
                  disabled={page >= totalPages}
                  className="p-2 rounded-lg border border-border hover:bg-surfaceSubtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detay Modal */}
      {detayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surfaceSubtle">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-textPrimary">Sipariş Detayı</h2>
                  <p className="text-sm text-textSecondary font-mono">{selectedSiparis?.siparisNo}</p>
                </div>
              </div>
              <button
                onClick={closeDetayModal}
                className="p-2 hover:bg-surfaceSubtle rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-textSecondary" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
              {detayLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : siparisDetay ? (
                <div className="p-6 space-y-6">
                  {/* Netsis bağlantı uyarısı */}
                  {siparisDetay.mesaj && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
                      <Clock className="h-4 w-4 text-orange-500 mt-0.5" />
                      <div>
                        <p className="text-xs text-orange-600 font-medium">Bilgi</p>
                        <p className="text-sm text-orange-700">{siparisDetay.mesaj}</p>
                      </div>
                    </div>
                  )}

                  {/* Sipariş Başlık Bilgileri - Cache veya Netsis'ten */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-surfaceSubtle rounded-lg p-3">
                      <p className="text-xs text-textSecondary">Tarih</p>
                      <p className="text-sm font-semibold text-textPrimary">{formatDate(siparisDetay.siparis?.tarih || selectedSiparis?.tarih)}</p>
                    </div>
                    <div className="bg-surfaceSubtle rounded-lg p-3">
                      <p className="text-xs text-textSecondary">Cari</p>
                      <p className="text-sm font-semibold text-textPrimary truncate">{siparisDetay.siparis?.cariIsim || siparisDetay.siparis?.cariKodu || selectedSiparis?.cari}</p>
                    </div>
                    <div className="bg-surfaceSubtle rounded-lg p-3">
                      <p className="text-xs text-textSecondary">Plasiyer</p>
                      <p className="text-sm font-semibold text-textPrimary">{siparisDetay.siparis?.plasiyer || selectedSiparis?.plasiyer || "-"}</p>
                    </div>
                    <div className="bg-surfaceSubtle rounded-lg p-3">
                      <p className="text-xs text-textSecondary">Genel Toplam</p>
                      <p className="text-sm font-bold text-primary">{formatCurrency(siparisDetay.siparis?.genelToplam || siparisDetay.siparis?.tutar || selectedSiparis?.tutar)}</p>
                    </div>
                  </div>

                  {(siparisDetay.siparis?.aciklama || selectedSiparis?.aciklama) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-600 font-medium">Açıklama</p>
                      <p className="text-sm text-yellow-800">{siparisDetay.siparis?.aciklama || selectedSiparis?.aciklama}</p>
                    </div>
                  )}

                  {/* Kalemler Tablosu - Sadece Netsis bağlıysa */}
                  {siparisDetay.toplamKalem > 0 ? (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="h-4 w-4 text-textSecondary" />
                        <h3 className="text-sm font-semibold text-textPrimary">Sipariş Kalemleri ({siparisDetay.toplamKalem})</h3>
                      </div>
                      <div className="overflow-x-auto border border-border rounded-lg">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-surfaceSubtle border-b border-border">
                              <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Stok Kodu</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Ürün Adı</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Marka</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Üretici Kodu</th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-textSecondary">Miktar</th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-textSecondary bg-orange-50 text-orange-700">Kalan</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold text-textSecondary">Birim Fiyat</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold text-textSecondary">Toplam</th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-textSecondary">Durum</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {siparisDetay.detaylar?.map((kalem, idx) => (
                            <tr 
                              key={idx} 
                              className="hover:bg-surfaceSubtle/50 cursor-pointer transition-colors"
                              onClick={() => openStokHareketModal(kalem.stokKodu, kalem.stokAdi)}
                              title="Stok hareketlerini görüntülemek için tıklayın"
                            >
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1">
                                  <Activity className="h-3 w-3 text-textSecondary/50" />
                                  <span className="font-mono text-xs text-primary hover:underline">{kalem.stokKodu}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <span className="text-sm text-textPrimary">{kalem.stokAdi}</span>
                              </td>
                              <td className="px-3 py-2">
                                {kalem.marka && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{kalem.marka}</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <span className="font-mono text-xs text-textSecondary">{kalem.ureticiKodu || "-"}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="text-sm font-medium text-textPrimary">{kalem.miktar}</span>
                              </td>
                              <td className="px-3 py-2 text-center bg-orange-50">
                                <span className={`text-sm font-bold ${kalem.kalanMiktar > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                  {kalem.kalanMiktar || 0}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <span className="text-sm text-textPrimary">{formatCurrency(kalem.birimFiyat)}</span>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <span className="text-sm font-semibold text-textPrimary">{formatCurrency(kalem.toplamTutar || (kalem.miktar * kalem.birimFiyat))}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                  kalem.durum === 'KAPALI' 
                                    ? 'bg-green-500/10 text-green-600' 
                                    : 'bg-orange-500/10 text-orange-600'
                                }`}>
                                  {kalem.durum === 'KAPALI' ? (
                                    <><CheckCircle className="h-3 w-3" /> Kapalı</>
                                  ) : (
                                    <><Clock className="h-3 w-3" /> Açık</>
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                      <Box className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Sipariş kalemleri Netsis bağlantısı olmadan gösterilemiyor.</p>
                      <p className="text-xs text-gray-500 mt-1">Production ortamında Netsis'e bağlanınca detaylar görünecektir.</p>
                    </div>
                  )}

                  {/* Alt Toplam - Sadece Netsis detayları varsa */}
                  {siparisDetay.toplamKalem > 0 && (
                    <div className="flex justify-end">
                      <div className="bg-surfaceSubtle rounded-lg p-4 min-w-[250px]">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-textSecondary">Brüt Tutar:</span>
                          <span className="text-textPrimary">{formatCurrency(siparisDetay.siparis?.brutTutar)}</span>
                        </div>
                        {siparisDetay.siparis?.iskontoTutar > 0 && (
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-textSecondary">İskonto:</span>
                            <span className="text-red-600">-{formatCurrency(siparisDetay.siparis?.iskontoTutar)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-textSecondary">KDV:</span>
                          <span className="text-textPrimary">{formatCurrency(siparisDetay.siparis?.kdvTutar)}</span>
                        </div>
                        <div className="flex justify-between text-base font-bold border-t border-border pt-2 mt-2">
                          <span className="text-textPrimary">Genel Toplam:</span>
                          <span className="text-primary">{formatCurrency(siparisDetay.siparis?.genelToplam)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <p className="text-textSecondary">Detay bulunamadı</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-border bg-surfaceSubtle">
              <button
                onClick={closeDetayModal}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stok Hareket Modal */}
      {stokHareketModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surfaceSubtle">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Activity className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-textPrimary">Stok Hareketi</h2>
                  <p className="text-sm text-textSecondary font-mono">{selectedStok?.stokKodu}</p>
                </div>
              </div>
              <button
                onClick={closeStokHareketModal}
                className="p-2 hover:bg-surfaceSubtle rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-textSecondary" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
              {stokHareketLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : stokHareket ? (
                <div className="p-6 space-y-6">
                  {/* Stok Bilgisi - Genişletilmiş */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <Box className="h-10 w-10 text-blue-600 mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-blue-600 bg-blue-100 px-2 py-0.5 rounded">{stokHareket.stok?.stokKodu}</span>
                          {stokHareket.stok?.marka && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{stokHareket.stok?.marka}</span>
                          )}
                        </div>
                        <p className="text-lg font-bold text-blue-800 mb-2">{stokHareket.stok?.stokAdi}</p>
                        {stokHareket.stok?.ureticiKodu && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-blue-600">Üretici Kodu:</span>
                            <span className="font-mono text-blue-800 bg-blue-100/50 px-2 py-0.5 rounded">{stokHareket.stok?.ureticiKodu}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stok Bakiye Özeti - Genişletilmiş */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="text-xs text-purple-600">Ön Bakiye (Devir)</p>
                          <p className="text-xl font-bold text-purple-700">{stokHareket.bakiye?.onBakiye || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <ArrowDownCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-xs text-green-600">Toplam Giriş</p>
                          <p className="text-xl font-bold text-green-700">{stokHareket.bakiye?.toplamGiris || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <ArrowUpCircle className="h-5 w-5 text-red-600" />
                        <div>
                          <p className="text-xs text-red-600">Toplam Çıkış</p>
                          <p className="text-xl font-bold text-red-700">{stokHareket.bakiye?.toplamCikis || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-xs text-blue-600">Mevcut Stok</p>
                          <p className="text-xl font-bold text-blue-700">{stokHareket.bakiye?.mevcutStok || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hareket Tablosu */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="h-4 w-4 text-textSecondary" />
                      <h3 className="text-sm font-semibold text-textPrimary">Son Hareketler ({stokHareket.toplamHareket})</h3>
                    </div>
                    <div className="overflow-x-auto border border-border rounded-lg">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-surfaceSubtle border-b border-border">
                            <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Tarih</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Belge No</th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-textSecondary">Tip</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Belge Tipi</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold text-textSecondary">Miktar</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold text-textSecondary bg-blue-50 text-blue-700">Bakiye</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold text-textSecondary">Birim Fiyat</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold text-textSecondary">Tutar</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-textSecondary">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {stokHareket.hareketler?.length === 0 ? (
                            <tr>
                              <td colSpan={9} className="px-3 py-8 text-center text-textSecondary">
                                Bu stok için hareket bulunamadı
                              </td>
                            </tr>
                          ) : (
                            stokHareket.hareketler?.map((hareket, idx) => (
                              <tr key={idx} className="hover:bg-surfaceSubtle/50">
                                <td className="px-3 py-2 text-sm text-textPrimary">{formatDate(hareket.tarih)}</td>
                                <td className="px-3 py-2">
                                  <span className="font-mono text-xs text-primary">{hareket.belgeNo || "-"}</span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                    hareket.hareketTipi === 'G' 
                                      ? 'bg-green-500/10 text-green-600' 
                                      : 'bg-red-500/10 text-red-600'
                                  }`}>
                                    {hareket.hareketTipi === 'G' ? (
                                      <><ArrowDownCircle className="h-3 w-3" /> Giriş</>
                                    ) : (
                                      <><ArrowUpCircle className="h-3 w-3" /> Çıkış</>
                                    )}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-sm text-textSecondary">{hareket.belgeTipiText}</td>
                                <td className="px-3 py-2 text-right">
                                  <span className={`text-sm font-medium ${hareket.hareketTipi === 'G' ? 'text-green-600' : 'text-red-600'}`}>
                                    {hareket.hareketTipi === 'G' ? '+' : '-'}{hareket.miktar}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right bg-blue-50">
                                  <span className="text-sm font-bold text-blue-700">
                                    {hareket.yuruyenBakiye}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right text-sm text-textPrimary">{formatCurrency(hareket.birimFiyat)}</td>
                                <td className="px-3 py-2 text-right text-sm font-semibold text-textPrimary">{formatCurrency(hareket.tutar)}</td>
                                <td className="px-3 py-2 text-sm text-textSecondary truncate max-w-[150px]" title={hareket.cariIsim || hareket.aciklama}>
                                  {hareket.cariIsim || hareket.aciklama || "-"}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <p className="text-textSecondary">Stok hareketi bulunamadı</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-border bg-surfaceSubtle">
              <button
                onClick={closeStokHareketModal}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
